import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import { findStaticWithdrawCoin } from "../../../api/services/withdrawStaticCoins";

/** Explorer / UI labels for chain codes from withdrawal catalog (`withdrawal-coins`) */
export const WITHDRAW_NETWORK_LABELS = {
  BEP20: "BNB Smart Chain (bsc)",
  ERC20: "Ethereum (ETH)",
  TRC20: "Tron (TRC20)",
  PLASMA: "Plasma (plasma)",
  TON: "TON (The Open Network) with Memo(ton)",
  POLYGON: "Polygon (MATIC)",
  BTC: "Bitcoin (BTC)",
  SOLANA: "Solana (SOL)",
};

export function extractWithdrawCoinsList(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.coins)) return d.coins;
  return [];
}

const DEFAULT_COIN_ICONS = {
  USDT: "/images/tether_icon.png",
  BTC: "/images/bitcoin_icon.png",
  ETH: "/images/eth_currency_img.png",
  BNB: "/images/AnnouncementImg/bnb_icon.svg",
};

const ICON_FIELD_KEYS = [
  "icon_path",
  "icon",
  "image",
  "logo",
  "image_url",
  "coin_image",
  "iconPath",
  "coinIcon",
  "img",
];

export function coinSymbol(coin) {
  return String(
    coin?.short_name || coin?.symbol || coin?.name || coin?.coin || coin?.currency || coin?.token || ""
  ).trim() || "—";
}

/**
 * Absolute media URL for coin icons — use as <img src={normalizeIconPathString(path)} />.
 * Prefer API fields that are already full S3/CDN https URLs.
 */
export function normalizeIconPathString(path) {
  if (path == null || path === "") return null;
  const u = resolvePublicUploadUrl(path);
  return u || null;
}

function pushIconStringsFromValue(out, v) {
  if (v == null) return;
  if (typeof v === "string" && v.trim()) {
    out.push(v.trim());
    return;
  }
  if (typeof v === "object" && !Array.isArray(v)) {
    for (const k of ICON_FIELD_KEYS) {
      if (v[k] != null && String(v[k]).trim()) out.push(String(v[k]).trim());
    }
  }
}

/**
 * Gathers every plausible raw path string from a coin and nested `currency_id` / `coin` docs.
 */
function collectRawIconPaths(coin) {
  if (coin == null) return [];
  if (typeof coin === "string") {
    return coin.trim() ? [coin.trim()] : [];
  }
  const out = [];
  pushIconStringsFromValue(out, coin);
  const nested = [
    coin.currency_id,
    coin.currencyId,
    coin.currency,
    coin.coin,
    coin.token,
    coin.token_id,
    coin.coin_id,
    coin.base_currency_id,
    coin.quote_currency_id,
  ];
  for (const n of nested) {
    if (n && typeof n === "object") {
      for (const k of ICON_FIELD_KEYS) {
        if (n[k] != null && String(n[k]).trim()) out.push(String(n[k]).trim());
      }
    }
  }
  return [...new Set(out)];
}

export function resolveCoinIconUrl(coinOrPath) {
  if (coinOrPath == null) return null;
  if (typeof coinOrPath === "string") return normalizeIconPathString(coinOrPath);
  for (const raw of collectRawIconPaths(coinOrPath)) {
    const u = normalizeIconPathString(raw);
    if (u) return u;
  }
  return null;
}

export function localSymbolFallbackSrc(coin) {
  if (!coin || typeof coin !== "object") return "/images/tether_icon.png";
  const sym = coinSymbol(coin).toUpperCase();
  if (sym && sym !== "—" && DEFAULT_COIN_ICONS[sym]) return DEFAULT_COIN_ICONS[sym];
  return "/images/tether_icon.png";
}

/**
 * Public image URL for a coin (or a raw `icon_path` string). Always returns a usable `src`, including
 * local static fallbacks so the UI is never empty when API omits or breaks remote URLs.
 */
export function coinIconSrc(coinOrPath) {
  const u = resolveCoinIconUrl(coinOrPath);
  if (u) return u;
  if (typeof coinOrPath === "object" && coinOrPath != null) {
    return localSymbolFallbackSrc(coinOrPath);
  }
  return "/images/tether_icon.png";
}

/** When a remote `src` 404s, swap to a local file (same as manual Trade pattern). */
export function makeCoinImageOnError(coin) {
  const fallback = localSymbolFallbackSrc(coin);
  return (e) => {
    const el = e?.currentTarget;
    if (!el) return;
    el.onerror = null;
    if (el.getAttribute("data-coin-fallback") !== "1") {
      el.setAttribute("data-coin-fallback", "1");
      el.src = fallback;
    }
  };
}

function isWithdrawChainActive(coin, code) {
  const statusMap =
    coin.withdrawal_status || coin.withdraw_status || coin.withdrawStatus || null;
  const w = statusMap && statusMap[code];
  if (w != null && String(w).trim() !== "") {
    return String(w).toUpperCase() === "ACTIVE";
  }
  const d = coin.deposit_status && coin.deposit_status[code];
  if (d != null && String(d).trim() !== "") {
    return String(d).toUpperCase() === "ACTIVE";
  }
  return true;
}

/**
 * Networks for withdrawal UI. If the API row has no `chain` / inactive maps, uses the
 * bundled static coin for the same symbol (see `withdrawStaticCoins.js`).
 */
export function getWithdrawNetworksOrStaticFallback(coin) {
  if (!coin) return [];
  const nets = getWithdrawNetworks(coin);
  if (nets.length) return nets;
  const staticCoin = findStaticWithdrawCoin(coin);
  if (!staticCoin) return [];
  return getWithdrawNetworks(staticCoin);
}

/** Networks for one coin (from `chain[]` + status maps + min/max). */
export function getWithdrawNetworks(coin) {
  if (!coin?.chain?.length) return [];
  const minW = coin.min_withdrawal || coin.min_withdraw || coin.min_deposit || {};
  const maxW = coin.max_withdrawal || coin.max_withdraw || coin.max_deposit || {};
  const feeMap = coin.withdrawal_fee || coin.withdraw_fee || coin.network_withdrawal_fee || {};
  return coin.chain
    .filter((c) => isWithdrawChainActive(coin, c))
    .map((code) => {
      const minRaw = minW[code];
      const maxRaw = maxW[code];
      const fr = feeMap[code];
      const fee =
        fr != null && fr !== "" && Number.isFinite(Number(fr)) && Number(fr) >= 0
          ? Number(fr)
          : null;
      return {
        code,
        label: coin.chain_full_names?.[code] || WITHDRAW_NETWORK_LABELS[code] || code,
        min: minRaw != null && minRaw !== "" ? String(minRaw) : "—",
        max: maxRaw != null && maxRaw !== "" ? String(maxRaw) : "—",
        fee,
        eta: "≈ 2 mins",
      };
    });
}

function firstFiniteNum(...candidates) {
  for (const v of candidates) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Human-readable number for limit / amount rows. */
export function formatWithdrawAmountDisplay(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  if (a === 0) return "0";
  if (a >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

/**
 * "remaining / cap" for the 24h line: wallet row → coin root → per-chain `max_withdrawal`.
 * If the API only sends per-chain max, shows `max / max` (same as full remaining in many UIs).
 */
export function get24hWithdrawLimitDisplay(coin, chainCode, fundRow) {
  if (!coin || !chainCode) return "— / —";
  const maxByChain = coin.max_withdrawal && chainCode && coin.max_withdrawal[chainCode] != null
    ? Number(coin.max_withdrawal[chainCode])
    : null;

  const cap = firstFiniteNum(
    fundRow?.daily_withdraw_limit,
    fundRow?.limit_24h,
    fundRow?.withdraw_24h_limit,
    coin?.daily_withdraw_limit,
    coin?.limit_24h,
    maxByChain
  );
  const rem = firstFiniteNum(
    fundRow?.remaining_24h_withdraw,
    fundRow?.remaining_24h,
    fundRow?.daily_withdraw_remaining,
    fundRow?.withdraw_24h_remaining,
    coin?.remaining_24h_withdraw,
    coin?.remaining_24h,
    coin?.daily_withdraw_remaining,
    chainCode && coin?.remaining_24h_by_chain && coin.remaining_24h_by_chain[chainCode],
    cap,
    maxByChain
  );

  const right = cap ?? maxByChain;
  const left = rem ?? right;

  if (right == null && left == null) return "— / —";
  if (right == null) return `${formatWithdrawAmountDisplay(left)} / —`;
  if (left == null) return `— / ${formatWithdrawAmountDisplay(right)}`;
  return `${formatWithdrawAmountDisplay(left)} / ${formatWithdrawAmountDisplay(right)}`;
}

export function extractWithdrawalHistoryList(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.withdrawals)) return d.withdrawals;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.transactions)) return d.transactions;
  if (d && Array.isArray(d.rows)) return d.rows;
  if (d && Array.isArray(d.list)) return d.list;
  return [];
}

/** Optional `total` when `data` wraps the list (pagination). */
export function withdrawalHistoryApiParse(res) {
  if (res && res.success === false) return { rawRows: [], total: null };
  const rawRows = extractWithdrawalHistoryList(res);
  const d = res?.data;
  let total = null;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const rawTotal = d.total ?? d.totalCount ?? d.count ?? d.total_count;
    const n = typeof rawTotal === "string" ? Number(rawTotal) : rawTotal;
    if (Number.isFinite(n)) total = n;
  }
  return { rawRows, total };
}

const FIAT_TICKERS = new Set(["INR", "USD", "EUR", "AED", "GBP", "TRY", "BRL"]);

export function inferWithdrawHistoryKind(raw) {
  const t = String(raw.type || raw.withdrawal_type || raw.asset_type || "").toLowerCase();
  if (t === "fiat" || raw.is_fiat === true || raw.fiat === true) return "Fiat";
  if (t === "crypto" || t === "coin") return "Crypto";
  const sym = String(raw.short_name || raw.coin || raw.currency || raw.token || "").toUpperCase();
  if (sym && FIAT_TICKERS.has(sym)) return "Fiat";
  return "Crypto";
}

function withdrawOrderDestination(raw, sourceFallback) {
  const d =
    raw.destination_wallet ||
    raw.to_wallet ||
    raw.wallet_to ||
    raw.receiver_wallet ||
    raw.internal_wallet ||
    raw.transfer_to ||
    raw.withdraw_to_wallet ||
    "";
  if (d) return String(d);
  return sourceFallback || "—";
}

function withdrawOrderTxSub(raw) {
  const c = raw.confirmations ?? raw.tx_confirmations ?? raw.network_confirmations ?? raw.confirms;
  if (c == null || c === "") return "";
  return String(c);
}

export function truncateMid(s, headLen = 8, tailLen = 4) {
  if (s == null || s === "—" || s === "") return "—";
  const str = String(s);
  if (str.length <= headLen + tailLen + 1) return str;
  return `${str.slice(0, headLen)}…${str.slice(-tailLen)}`;
}

export function formatWithdrawHistoryDate(v) {
  if (v == null || v === "") return "—";
  try {
    const t = new Date(v);
    if (Number.isNaN(t.getTime())) return String(v);
    return t.toLocaleString();
  } catch {
    return String(v);
  }
}

/** Stepper timestamps — `07/05/2026, 21:41:34` style (en-GB, 24h). */
function formatWithdrawHistoryStepTime(v) {
  if (v == null || v === "") return "—";
  try {
    const t = new Date(v);
    if (Number.isNaN(t.getTime())) return String(v);
    return t.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch {
    return String(v);
  }
}

/**
 * True while funds are locked and admin has not sent to Fireblocks yet.
 * Prefer API `awaiting_admin`; otherwise infer from PENDING/TRAVEL_RULE_REVIEW + empty Fireblocks id.
 */
export function isWithdrawAwaitingAdmin(raw) {
  if (!raw || typeof raw !== "object") return false;
  if (raw.awaiting_admin === true) return true;
  if (raw.awaiting_admin === false) return false;
  const status = String(raw.status || raw.transaction_status || raw.state || "")
    .trim()
    .toUpperCase()
    .replace(/-/g, "_");
  if (status === "TRAVEL_RULE_REVIEW") return true;
  if (status !== "PENDING") return false;
  const fb = raw.fireblocks_transaction_id ?? raw.fireblocksTxId ?? "";
  return fb == null || String(fb).trim() === "";
}

/** Display label for API withdrawal status (FAILED → Failed, etc.). */
export function formatWithdrawHistoryStatus(raw, extras = {}) {
  if (raw == null || raw === "") return "—";
  const s = String(raw).trim();
  const up = s.toUpperCase().replace(/-/g, "_");
  if (extras.awaitingAdmin && (up === "PENDING" || up === "TRAVEL_RULE_REVIEW")) {
    return up === "TRAVEL_RULE_REVIEW" ? "Under review" : "Pending approval";
  }
  const map = {
    FAILED: "Failed",
    FAILURE: "Failed",
    ERROR: "Failed",
    SUCCESS: "Completed",
    COMPLETED: "Completed",
    CONFIRMED: "Confirmed",
    DONE: "Completed",
    PENDING: "Processing",
    AWAITING_DECLARATION: "Confirm who sent this withdrawal",
    PROCESSING: "Processing",
    BROADCASTING: "Processing",
    CONFIRMING: "Processing",
    QUEUED: "Queued",
    REVIEW: "Under review",
    UNDER_REVIEW: "Under review",
    TRAVEL_RULE_REVIEW: "Under review",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    REJECT: "Rejected",
    CANCELLED: "Cancelled",
    CANCELED: "Cancelled",
  };
  if (map[up]) return map[up];
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const UUID_TX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Block explorer links only for plausible on-chain hashes (UUID internal refs are not on-chain). */
export function withdrawHistoryTxExplorerEligible(txid) {
  if (txid == null || txid === "") return false;
  const s = String(txid).trim();
  if (UUID_TX_RE.test(s)) return false;
  if (/^0x[a-fA-F0-9]{64}$/.test(s)) return true;
  if (/^[a-fA-F0-9]{64}$/.test(s)) return true;
  return false;
}

function withdrawHistoryOutcomeTone(statusUpper) {
  const u = String(statusUpper || "").toUpperCase().replace(/-/g, "_");
  if (/^(FAILED|FAILURE|ERROR|REJECTED|REJECT|CANCELLED|CANCELED)$/.test(u)) return "danger";
  if (/^(COMPLETED|SUCCESS|CONFIRMED|DONE)$/.test(u)) return "success";
  return "pending";
}

function buildWithdrawHistorySteps({ created, updated, statusUpper, statusLabel, awaitingAdmin }) {
  const tSubmit = formatWithdrawHistoryStepTime(created);
  const tOutcome = formatWithdrawHistoryStepTime(updated || created);
  const u = String(statusUpper || "").toUpperCase().replace(/-/g, "_");

  const submitted = { title: "Withdrawal request submitted", time: tSubmit };

  if (u === "AWAITING_DECLARATION") {
    return [
      submitted,
      {
        title: "Confirm who is sending this withdrawal",
        subtitle: "Funds are locked. Submit the form before an admin can approve this request.",
        time: tOutcome,
        emphasized: true,
      },
    ];
  }

  if (awaitingAdmin) {
    return [
      submitted,
      {
        title: u === "TRAVEL_RULE_REVIEW" ? "Under compliance review" : "Waiting for admin approval",
        subtitle:
          u === "TRAVEL_RULE_REVIEW"
            ? "This withdrawal is held for travel-rule / compliance review. You can cancel it until it is approved."
            : "Funds are locked. You can cancel until an admin approves this request.",
        time: tOutcome,
        emphasized: true,
      },
    ];
  }

  if (/^(FAILED|FAILURE|ERROR)$/.test(u)) {
    return [
      submitted,
      {
        title: "Withdrawal failed",
        subtitle:
          "This transfer did not complete on-chain. If any balance was reserved, it is usually returned to your main wallet shortly.",
        time: tOutcome,
        emphasized: true,
      },
    ];
  }
  if (/^(REJECTED|REJECT)$/.test(u)) {
    return [
      submitted,
      {
        title: "Withdrawal rejected",
        subtitle: "This request was not approved. Check your email or contact support for details.",
        time: tOutcome,
        emphasized: true,
      },
    ];
  }
  if (/^(CANCELLED|CANCELED)$/.test(u)) {
    return [
      submitted,
      {
        title: "Withdrawal cancelled",
        time: tOutcome,
        emphasized: true,
      },
    ];
  }
  if (/^(COMPLETED|SUCCESS|CONFIRMED|DONE)$/.test(u)) {
    return [
      submitted,
      {
        title: "Transfer completed",
        time: tOutcome,
        emphasized: true,
      },
    ];
  }

  return [
    submitted,
    {
      title: statusLabel && statusLabel !== "—" ? `${statusLabel}` : "Processing withdrawal",
      subtitle: "You will receive an email when this withdrawal completes.",
      time: tOutcome,
      emphasized: true,
    },
  ];
}

function rowIconSrc(r) {
  return coinIconSrc(r);
}

/**
 * Normalize one API withdrawal row for Recent list + detail modal.
 */
export function mapWithdrawHistoryRow(r, i) {
  const amount =
    r.amount ??
    r.net_amount ??
    r.withdrawal_amount ??
    r.withdraw_amount ??
    r.quantity;
  const chain = r.chain || r.network || r.selected_chain || "";
  const addr = r.withdrawal_address || r.address || r.to_address || "";
  const tx = r.transaction_hash || r.txid || r.txId || r.tx_id || r.hash;
  const shortName = r.short_name || r.coin || r.currency || r.token || "—";
  const created = r.createdAt || r.created_at || r.time || r.date;
  const updated = r.updatedAt || r.updated_at;
  const statusRaw = String(r.status || r.transaction_status || r.state || "").trim();
  const statusUpper = statusRaw ? statusRaw.toUpperCase().replace(/-/g, "_") : "";
  const awaitingAdmin = isWithdrawAwaitingAdmin(r);
  const declarationStatus = String(r.declaration_status ?? "").trim().toUpperCase();
  const needsSenderConfirm = statusUpper === "AWAITING_DECLARATION" && declarationStatus !== "SUBMITTED";
  const statusLabel = statusRaw ? formatWithdrawHistoryStatus(statusRaw, { awaitingAdmin }) : "—";
  const currencyId = r.currency_id ?? r.currencyId ?? null;

  const addrDisp = addr ? truncateMid(addr, 10, 6) : "—";
  const txDisp = tx ? truncateMid(String(tx), 10, 6) : "—";
  const listDateStr = formatWithdrawHistoryDate(created);
  const txStr = tx ? String(tx) : "";
  const networkLabel =
    r.chain_full_name != null && String(r.chain_full_name).trim()
      ? String(r.chain_full_name)
      : String(chain || "—").toUpperCase();
  const sourceWalletLabel = r.wallet || r.walletType || r.wallet_type || "Main Wallet";
  const symU = String(shortName).toUpperCase();
  const coinName =
    r.currency_name ||
    r.currency_full_name ||
    r.name ||
    r.token_name ||
    r.coin_name ||
    symU;
  const kind = inferWithdrawHistoryKind(r);
  const dest = withdrawOrderDestination(r, sourceWalletLabel);
  const txSubLine = withdrawOrderTxSub(r);
  const amtStr = amount != null && amount !== "" ? String(amount) : "—";

  return {
    id: r._id || r.id || tx || `w-${i}`,
    coin: symU,
    coinIconSrc: rowIconSrc(r),
    listDate: listDateStr,
    date: listDateStr,
    amount: amtStr,
    network: String(chain || "—").toUpperCase(),
    rawChain: String(chain || "").trim(),
    networkLabel,
    tokenAssetId: r.tokenAssetId != null ? String(r.tokenAssetId) : "",
    addressShort: addrDisp,
    addressDisplay: addrDisp,
    addressFull: addr || "",
    txShort: txDisp,
    txDisplay: txDisp,
    txFull: txStr,
    txExplorerEligible: withdrawHistoryTxExplorerEligible(txStr),
    statusLabel,
    needsSenderConfirm,
    awaitingAdmin,
    canCancel: awaitingAdmin,
    currencyId: currencyId != null ? String(currencyId) : "",
    historyOutcomeTone: statusUpper ? withdrawHistoryOutcomeTone(statusUpper) : "pending",
    networkFee: r.fee != null ? String(r.fee) : r.network_fee != null ? String(r.network_fee) : "—",
    source: sourceWalletLabel,
    // Only show "Unverified" when API explicitly marks it (otherwise it was always true).
    addressUnverified: Boolean(
      r.unverified === true ||
      r.address_unverified === true ||
      r.is_unverified === true ||
      r.whitelist_verified === false
    ),
    steps: buildWithdrawHistorySteps({
      created,
      updated,
      statusUpper,
      statusLabel,
      awaitingAdmin,
    }),
    /** Profile → Orders → Withdraw history table + client filters */
    orderTable: {
      time: listDateStr,
      type: kind,
      wallet: addr ? addrDisp : "—",
      coin: coinName !== "—" ? String(coinName) : symU,
      coinSub: symU,
      /** Network / chain for orders table (full name from API when present). */
      chain: networkLabel,
      amount: amtStr,
      destination: dest,
      txid: txDisp,
      txidFull: txStr,
      txSub: txSubLine,
      status: statusLabel,
    },
  };
}

/**
 * Find fund row by short name (e.g. USDT) in getUserfunds array.
 */
export function findFundRowBySymbol(fundRows, shortName) {
  if (!Array.isArray(fundRows) || !shortName) return null;
  const sym = String(shortName).toUpperCase();
  return fundRows.find((row) => String(row.short_name || row.currency || "").toUpperCase() === sym) || null;
}

export function formatFundAvailable(row) {
  if (!row) return "0.00";
  const b = (parseFloat(row.balance) || 0) + (parseFloat(row.locked_balance) || 0);
  if (!Number.isFinite(b)) return "0.00";
  if (b === 0) return "0.00";
  return b >= 1 ? b.toFixed(2) : b.toFixed(6);
}

/** Second column hint in coin picker (min withdraw on first network). */
export function coinWithdrawListHint(coin) {
  const nets = getWithdrawNetworksOrStaticFallback(coin);
  if (!nets.length) return "—";
  const n = nets[0];
  const sym = coinSymbol(coin);
  if (n.min === "—") return "—";
  return `${n.min} ${sym}`.trim();
}

/** Parse `POST /v1/wallet/get-and-generate-address` response (aligned with Deposit page). */
export function parseWalletDepositAddressResponse(res) {
  const rootData = res?.data;
  const d =
    rootData && typeof rootData === "object" && !Array.isArray(rootData) && rootData.data != null
      ? rootData.data
      : rootData;
  if (d == null) return { address: "", memo: "" };
  if (typeof d === "string") return { address: d, memo: "" };
  if (typeof d === "object") {
    const raw =
      d.address ||
      d.depositAddress ||
      d.deposit_address ||
      d.wallet_address ||
      d.walletAddress ||
      d.base58Address ||
      d.base58_address ||
      d.tronAddress ||
      d.tron_address ||
      d.receiveAddress ||
      d.receive_address ||
      "";
    const mem = [d.memo, d.tag, d.destinationTag, d.destination_tag, d.memoTag].find(
      (x) => x != null && String(x).length > 0
    );
    return { address: String(raw), memo: mem != null ? String(mem) : "" };
  }
  return { address: "", memo: "" };
}

/**
 * Map UI / catalog chain code to canonical `chain` for withdrawal + validate-address APIs.
 * See product guide: BEP20, ERC20, TRC20, MATIC, SOL, …
 */
export function canonicalWithdrawalChainFromUiCode(uiCode) {
  if (uiCode == null || uiCode === "" || uiCode === "Select Network") return "";
  const u = String(uiCode).trim().toUpperCase();
  const map = {
    BSC: "BEP20",
    BNB: "BEP20",
    "BNB SMART CHAIN": "BEP20",
    BEP20: "BEP20",
    ETH: "ERC20",
    ETHEREUM: "ERC20",
    ERC20: "ERC20",
    TRX: "TRC20",
    TRON: "TRC20",
    TRC20: "TRC20",
    POLYGON: "MATIC",
    MATIC: "MATIC",
    SOL: "SOL",
    SOLANA: "SOL",
  };
  return map[u] || u;
}

/**
 * Match `GET /withdrawal-address-history` `fireblocks_asset_id` to a withdrawal catalog `token_asset_ids` chain code.
 */
export function findWithdrawNetworkCodeByFireblocksAssetId(coin, fireblocksAssetId) {
  const fid = String(fireblocksAssetId || "").trim();
  if (!coin || !fid) return "";
  const map = coin.token_asset_ids;
  if (!map || typeof map !== "object") return "";
  const fidU = fid.toUpperCase();
  for (const [chainCode, assetId] of Object.entries(map)) {
    if (String(assetId || "").trim().toUpperCase() === fidU) return String(chainCode).trim();
  }
  return "";
}

/**
 * Resolve withdrawal UI `network` code for a history row and selected coin (Fireblocks id first, then chain match).
 */
export function resolveWithdrawHistoryNetworkForCoin(entry, coin) {
  if (!entry || !coin) return "";
  const fromAsset = findWithdrawNetworkCodeByFireblocksAssetId(coin, entry.fireblocks_asset_id);
  if (fromAsset) return fromAsset;
  const entryChainRaw = entry.chain != null ? String(entry.chain).trim() : "";
  if (!entryChainRaw) return "";
  const entryCanon = canonicalWithdrawalChainFromUiCode(entryChainRaw) || entryChainRaw.toUpperCase();
  const nets = getWithdrawNetworksOrStaticFallback(coin);
  for (const n of nets) {
    const nc = canonicalWithdrawalChainFromUiCode(n.code) || String(n.code).toUpperCase();
    if (nc && nc === entryCanon) return n.code;
    if (String(n.code).toUpperCase() === entryChainRaw.toUpperCase()) return n.code;
  }
  return "";
}

/** EVM chains that support MetaMask signature for withdrawal address whitelisting (non-EVM: Satoshi only). */
const METAMASK_WHITELIST_CHAIN_SET = new Set([
  "BEP20",
  "ERC20",
  "MATIC",
  "POLYGON",
  "ARB",
  "ARBITRUM",
  "OPT",
  "OPTIMISM",
  "BASE",
  "LINEA",
  "SCROLL",
]);

export function isMetaMaskWhitelistChain(canonicalChain) {
  const c = String(canonicalChain || "").trim().toUpperCase();
  return METAMASK_WHITELIST_CHAIN_SET.has(c);
}

/** Compare withdrawal / saved-book addresses (EVM hex is case-insensitive). */
export function addressesEqualForWithdrawalBookMatch(a, b) {
  const x = String(a ?? "").trim();
  const y = String(b ?? "").trim();
  if (!x || !y) return false;
  if (x.startsWith("0x") && y.startsWith("0x")) return x.toLowerCase() === y.toLowerCase();
  return x === y;
}
